/**
 * Safe Developer Certification Auth Fixture
 * 
 * Strictly gated behind DEVELOPMENT mode and explicit certification activation switch.
 * Provides the certified developer test identity (mock-user-002) for automated Playwright testing.
 * 
 * Invariants:
 * - Zero passwords
 * - Zero production mutations
 * - Disabled by default
 * - Blocked in production (fails closed)
 * - Rejects any identity other than the certified test identity
 */

export const CERT_UID = 'mock-user-002';
export const CERT_EMAIL = 'test@example.com';
export const CERT_TOKEN = 'mock-verified';

export interface DevCertUser {
    uid: string;
    email: string;
    emailVerified: boolean;
    isAnonymous: boolean;
    getIdToken: () => Promise<string>;
}

export function isDevCertModeActive(): boolean {
    // 1. Production guard: MUST be in development mode
    if (!import.meta.env.DEV || import.meta.env.PROD || import.meta.env.MODE === 'production') {
        return false;
    }

    // 2. Explicit enable guard: off by default, must be explicitly toggled in session/window
    if (typeof window === 'undefined') return false;
    try {
        const sessionSwitch = window.sessionStorage?.getItem('wingman_cert_auth_active') === 'true';
        const windowSwitch = (window as any).__WINGMAN_CERT_AUTH_ENABLED__ === true;
        return sessionSwitch || windowSwitch;
    } catch {
        return false;
    }
}

export function getDevCertUser(requestedUid: string = CERT_UID): DevCertUser | null {
    // Guard checks
    if (!isDevCertModeActive()) {
        return null;
    }

    // 3. Identity allowlist guard: reject arbitrary identities
    if (requestedUid !== CERT_UID) {
        console.error(`[DevCertAuth] Rejected unauthorized identity request: ${requestedUid}`);
        return null;
    }

    return {
        uid: CERT_UID,
        email: CERT_EMAIL,
        emailVerified: true,
        isAnonymous: false,
        getIdToken: async () => CERT_TOKEN
    };
}
